import { Logger } from "~/tools/logger";
import { round } from "~/tools/round";

import type { BorgStreamEvent, BorgStreamEventMetadata } from "../models/borg-stream-types";

export interface StreamSessionMetrics {
	startedAtMs: number;
	firstEventAtMs?: number;
	totalEvents: number;
	textDeltaEvents: number;
	thinkingDeltaEvents: number;
	toolCallStartEvents: number;
	toolCallCompleteEvents: number;
	snapshotEvents: number;
	errorEvents: number;
	doneEvents: number;
	gapCount: number;
	lastSequence?: number;
	reconnectCount: number;
	finalized: boolean;
}

export type StreamTerminationReason = "done" | "error" | "aborted" | "gap";

const logger = Logger.namespace("ai-assistant-drawer:stream");

export const createStreamSessionMetrics = (reconnectCount: number): StreamSessionMetrics => ({
	startedAtMs: performance.now(),
	totalEvents: 0,
	textDeltaEvents: 0,
	thinkingDeltaEvents: 0,
	toolCallStartEvents: 0,
	toolCallCompleteEvents: 0,
	snapshotEvents: 0,
	errorEvents: 0,
	doneEvents: 0,
	gapCount: 0,
	reconnectCount,
	finalized: false,
});

const addEventToMetrics = (metrics: StreamSessionMetrics, event: BorgStreamEvent): StreamSessionMetrics => {
	switch (event.type) {
		case "text_delta":
			return { ...metrics, textDeltaEvents: metrics.textDeltaEvents + 1 };
		case "thinking_delta":
			return { ...metrics, thinkingDeltaEvents: metrics.thinkingDeltaEvents + 1 };
		case "tool_call_start":
			return { ...metrics, toolCallStartEvents: metrics.toolCallStartEvents + 1 };
		case "tool_call_complete":
			return { ...metrics, toolCallCompleteEvents: metrics.toolCallCompleteEvents + 1 };
		case "stream_reset":
		case "progress":
		case "agent_handover":
		case "message_complete":
			return metrics;
		case "snapshot":
			return { ...metrics, snapshotEvents: metrics.snapshotEvents + 1 };
		case "error_event":
			return { ...metrics, errorEvents: metrics.errorEvents + 1 };
		case "done":
			return { ...metrics, doneEvents: metrics.doneEvents + 1 };
	}
};

const applySequenceMetadata = (
	metrics: StreamSessionMetrics,
	metadata?: BorgStreamEventMetadata,
): StreamSessionMetrics => {
	if (metadata?.sequence == null) {
		return metrics;
	}

	const gapCount =
		metrics.lastSequence != null && metadata.sequence > metrics.lastSequence + 1
			? metrics.gapCount + metadata.sequence - metrics.lastSequence - 1
			: metrics.gapCount;

	return {
		...metrics,
		gapCount,
		lastSequence: metadata.sequence,
	};
};

export const updateStreamSessionMetrics = (
	metrics: StreamSessionMetrics | null,
	event: BorgStreamEvent,
	metadata?: BorgStreamEventMetadata,
): StreamSessionMetrics | null => {
	if (metrics == null || metrics.finalized) {
		return metrics;
	}
	const firstEventAtMs = metrics.firstEventAtMs ?? performance.now();
	const updatedMetrics = addEventToMetrics(
		{
			...metrics,
			firstEventAtMs,
			totalEvents: metrics.totalEvents + 1,
		},
		event,
	);

	return applySequenceMetadata(updatedMetrics, metadata);
};

export const finalizeStreamSessionMetrics = (
	metrics: StreamSessionMetrics | null,
	conversationId: string | undefined,
	reason: StreamTerminationReason,
): StreamSessionMetrics | null => {
	if (metrics == null || metrics.finalized) {
		return metrics;
	}
	const finalizedMetrics = { ...metrics, finalized: true };
	const completedAtMs = performance.now();
	logger.debug("stream session metrics", {
		conversationId,
		reason,
		durationMs: round(completedAtMs - finalizedMetrics.startedAtMs, 0),
		timeToFirstEventMs:
			finalizedMetrics.firstEventAtMs == null
				? undefined
				: round(finalizedMetrics.firstEventAtMs - finalizedMetrics.startedAtMs, 0),
		totalEvents: finalizedMetrics.totalEvents,
		textDeltaEvents: finalizedMetrics.textDeltaEvents,
		thinkingDeltaEvents: finalizedMetrics.thinkingDeltaEvents,
		toolCallStartEvents: finalizedMetrics.toolCallStartEvents,
		toolCallCompleteEvents: finalizedMetrics.toolCallCompleteEvents,
		snapshotEvents: finalizedMetrics.snapshotEvents,
		errorEvents: finalizedMetrics.errorEvents,
		doneEvents: finalizedMetrics.doneEvents,
		gapCount: finalizedMetrics.gapCount,
		lastSequence: finalizedMetrics.lastSequence,
		reconnectCount: finalizedMetrics.reconnectCount,
	});

	return finalizedMetrics;
};
